import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, ToggleLeft, AlertTriangle } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const FEATURES = ["documents", "ai", "legal", "market", "deal_analyzer", "portfolio_optimizer", "due_diligence", "rent_billing"];
const DEFAULT_PLAN_KEYS = ["basic", "pro", "business"];

const emptyForm = { key: "", name: "", price_monthly: 0, price_yearly: "", property_limit: 3, active: true, features: {} };

export default function AdminPlans() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: plans = [] } = useQuery({
    queryKey: ["admin-plans"], queryFn: async () => (await api.get("/admin/plans")).data,
  });
  const { data: flags = [] } = useQuery({
    queryKey: ["admin-feature-flags"], queryFn: async () => (await api.get("/admin/feature-flags")).data,
  });

  const savePlan = useMutation({
    mutationFn: (payload) => editingKey
      ? api.put(`/admin/plans/${editingKey}`, payload)
      : api.post("/admin/plans", payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      setOpen(false);
      if (res.data?.stripe_price_changed) toast.success(t("price_change_warning"));
      else toast.success("OK");
    },
    onError: (e) => toast.error(apiErrorMessage(e?.response?.data?.detail)),
  });

  const deletePlan = useMutation({
    mutationFn: (key) => api.delete(`/admin/plans/${key}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-plans"] }); toast.success("OK"); },
    onError: (e) => toast.error(apiErrorMessage(e?.response?.data?.detail)),
  });

  const toggleFlag = useMutation({
    mutationFn: ({ key, enabled }) => api.put(`/admin/feature-flags/${key}?enabled=${enabled}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-feature-flags"] }),
  });

  const openCreate = () => { setEditingKey(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditingKey(p.key);
    setForm({ key: p.key, name: p.name, price_monthly: p.price_monthly,
             price_yearly: p.price_yearly ?? "", property_limit: p.property_limit,
             active: p.active, features: p.features || {} });
    setOpen(true);
  };

  const submit = () => {
    if (!form.key || !form.name) return toast.error(t("name"));
    savePlan.mutate({
      key: form.key, name: form.name,
      price_monthly: parseFloat(form.price_monthly) || 0,
      price_yearly: form.price_yearly === "" || form.price_yearly == null ? null : parseFloat(form.price_yearly),
      property_limit: parseInt(form.property_limit) || 0,
      active: !!form.active, features: form.features || {},
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl">{t("admin_plans_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin_plans_sub")}</p>
        </div>
        <Button onClick={openCreate} data-testid="new-plan-btn" className="rounded-sm gap-2">
          <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("new_plan")}</span>
        </Button>
      </div>

      <Card className="rounded-sm border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("plan_key")}</TableHead>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("plan_price")}</TableHead>
              <TableHead>{t("plan_price_yearly")}</TableHead>
              <TableHead>{t("plan_property_limit")}</TableHead>
              <TableHead>{t("plan_features")}</TableHead>
              <TableHead>{t("plan_status")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.key} data-testid={`admin-plan-${p.key}`}>
                <TableCell className="font-mono text-xs text-muted-foreground">{p.key}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="tabular-nums">{fmtCurrency(p.price_monthly)}</TableCell>
                <TableCell className="tabular-nums">{p.price_yearly == null ? "—" : fmtCurrency(p.price_yearly)}</TableCell>
                <TableCell className="tabular-nums">{p.property_limit < 0 ? t("unlimited") : p.property_limit}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {FEATURES.filter((f) => p.features?.[f]).map((f) => (
                      <Badge key={f} variant="secondary" className="rounded-sm text-[10px]">{t(`feat_${f}`)}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={p.active ? "default" : "outline"} className="rounded-sm">
                    {p.active ? t("active") : "—"}
                  </Badge>
                </TableCell>
                <TableCell className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.key}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!DEFAULT_PLAN_KEYS.includes(p.key) && (
                    <Button variant="ghost" size="icon" onClick={() => deletePlan.mutate(p.key)} data-testid={`delete-plan-${p.key}`}>
                      <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="rounded-sm border-border overflow-hidden" data-testid="feature-flags-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <ToggleLeft className="h-4 w-4 text-muted-foreground" />
          <div>
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("feature_flags_title")}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t("feature_flags_sub")}</p>
          </div>
        </div>
        <div className="divide-y divide-border">
          {flags.map((f, i) => (
            <motion.div key={f.key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
              className="flex items-center justify-between px-6 py-3" data-testid={`flag-${f.key}`}>
              <span className="text-sm">{f.label}</span>
              <Switch checked={f.enabled} onCheckedChange={(enabled) => toggleFlag.mutate({ key: f.key, enabled })} />
            </motion.div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("edit_plan")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {!editingKey && (
              <div className="space-y-2">
                <Label>{t("plan_key")}</Label>
                <Input data-testid="plan-key-input" value={form.key}
                      onChange={(e) => setForm({ ...form, key: e.target.value.trim().toLowerCase() })} className="rounded-sm" />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("name")}</Label>
              <Input data-testid="plan-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("plan_price")} ({t("billing_interval_monthly")})</Label>
                <Input type="number" data-testid="plan-price-input" value={form.price_monthly}
                      onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>{t("plan_price")} ({t("billing_interval_annual")})</Label>
                <Input type="number" data-testid="plan-price-yearly-input" value={form.price_yearly}
                      onChange={(e) => setForm({ ...form, price_yearly: e.target.value })} className="rounded-sm"
                      placeholder="—" />
              </div>
              <div className="space-y-2">
                <Label>{t("plan_property_limit")}</Label>
                <Input type="number" data-testid="plan-limit-input" value={form.property_limit}
                      onChange={(e) => setForm({ ...form, property_limit: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            {editingKey && Number(form.price_monthly) !== Number((plans.find((p) => p.key === editingKey) || {}).price_monthly) && (
              <div className="flex items-start gap-2 p-3 rounded-sm bg-[hsl(var(--warning))]/10 text-xs text-[hsl(var(--warning))]">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                {t("price_change_warning")}
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("plan_features")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {FEATURES.map((f) => (
                  <label key={f} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={!!form.features?.[f]}
                      data-testid={`feature-check-${f}`}
                      onCheckedChange={(v) => setForm({ ...form, features: { ...form.features, [f]: !!v } })}
                    />
                    {t(`feat_${f}`)}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!form.active} data-testid="plan-active-check"
                       onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("cancel")}</Button>
            <Button onClick={submit} data-testid="plan-save-btn" disabled={savePlan.isPending} className="rounded-sm">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
